import { usePageMeta } from "@/lib/seo";
import { IMAGES, ROADMAP, INDICATORS, OUTCOMES } from "@/content/site";
import { Reveal, SectionHeader, StagePill, CtaLink, IconTile } from "@/components/site/primitives";
import { CtaBand } from "@/components/site/CtaBand";

const Hero = () => (
  <section data-testid="vision-hero" className="relative overflow-hidden">
    <img src={IMAGES.aerial} alt="Aerial view of a rural village surrounded by green fields" className="absolute inset-0 h-full w-full object-cover" fetchpriority="high" />
    <div className="absolute inset-0 bg-gradient-to-r from-plum-900/90 via-plum-900/70 to-plum-900/20" aria-hidden="true" />
    <div className="container-x relative py-24 lg:py-36">
      <Reveal className="max-w-3xl">
        <StagePill className="!border-white/25 !bg-white/10 !text-white">Long-term ambition · Not yet achieved</StagePill>
        <p className="eyebrow mt-8 text-teal-100">Our vision & impact</p>
        <h1 className="mt-4 text-4xl font-semibold leading-[1.05] text-white sm:text-5xl lg:text-6xl">A Future Where Healthcare Is Closer to Every Community.</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">Our long-term ambition is to build a trusted, connected primary healthcare network — one that helps families reach qualified care early, close to home, and at a cost they can sustain.</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <CtaLink to="/partners#enquiry" testId="vision-hero-partner-cta">Partner With Us</CtaLink>
          <CtaLink to="#roadmap" testId="vision-hero-roadmap-cta" variant="ghostLight" arrow={false}>View the roadmap</CtaLink>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function VisionPage() {
  usePageMeta({
    title: "Vision & Impact — Roadmap from a Bihar Pilot",
    description: "SwastSaathi's long-term vision, four-phase expansion roadmap starting with a focused Bihar pilot, and the indicators we intend to use to measure impact.",
    path: "/vision-impact",
  });
  return (
    <>
      <Hero />
      <section id="roadmap" data-testid="vision-roadmap" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Expansion roadmap" title="Grow only as fast as the evidence allows." intro="Each phase begins only after the previous one has been validated. No timelines are promised — progress depends on what the pilot teaches us." />
          <ol className="relative mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {ROADMAP.map((r, i) => (
              <Reveal as="li" key={r.phase} delay={i * 0.08}>
                <article data-testid={`roadmap-phase-${i + 1}`} className={`relative h-full overflow-hidden rounded-2xl p-7 ${i === 0 ? "bg-teal text-white" : "card-soft"}`}>
                  <span className={`font-display text-6xl font-semibold ${i === 0 ? "text-white/20" : "text-plum/10"}`}>0{i + 1}</span>
                  <p className={`mt-4 text-xs font-semibold uppercase tracking-[0.16em] ${i === 0 ? "text-white/80" : "text-teal-700"}`}>{r.phase}{i === 0 && " · Next step"}</p>
                  <h3 className={`mt-2 text-xl font-semibold ${i === 0 ? "text-white" : ""}`}>{r.title}</h3>
                  <p className={`mt-3 leading-relaxed ${i === 0 ? "text-white/85" : "text-slate"}`}>{r.body}</p>
                </article>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section data-testid="vision-outcomes" className="bg-mint py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <SectionHeader className="lg:col-span-5" eyebrow="Intended outcomes" title="What success would look like for communities." intro="Intended outcomes, not proven results." />
          <div className="lg:col-span-7">
            <ul className="space-y-3">
              {OUTCOMES.map((o, i) => (
                <Reveal as="li" key={o.title} delay={i * 0.05}>
                  <div className="flex items-center gap-4 rounded-2xl bg-white p-4 pr-6">
                    <IconTile icon={o.icon} />
                    <p className="font-display text-lg font-medium text-plum">{o.title}</p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="vision-indicators" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHeader eyebrow="Impact measurement framework" title="How we intend to measure progress." />
            <Reveal><p className="max-w-sm text-sm leading-relaxed text-slate">Future indicators only. No impact data exists yet — we will begin measuring once the pilot is under way, and report honestly.</p></Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDICATORS.map((m, i) => (
              <Reveal key={m.title} delay={(i % 3) * 0.07}>
                <article data-testid={`indicator-${i}`} className="card-soft card-soft-hover flex h-full flex-col p-7">
                  <div className="flex items-center justify-between">
                    <IconTile icon={m.icon} tone={i % 2 ? "plum" : "teal"} />
                    <span className="rounded-full border border-dashed border-plum/25 px-3 py-1 text-xs font-semibold text-plum/70">To be measured</span>
                  </div>
                  <h3 className="mt-6 text-xl font-semibold">{m.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate">{m.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand testId="vision-cta" />
    </>
  );
}
